/**
 * The queue's filter panel — Step 42.
 *
 * Kai: *"The filter UI is a bit crap compared to freshservice, can we fix"* —
 * and the capture says exactly why. `Tickets _ Reference5.html` shows the
 * reference's panel as **named sections**, one per field, each with its own
 * picker:
 *
 *     View     New & Open tickets · All tickets · Unassigned
 *     Group    Group id · My Groups
 *     Tags     Engineering Backlog
 *
 * Mine was a stack of identical field / operator / value rows. That is the
 * right shape for BUILDING an expression and the wrong shape for the job
 * people actually do ninety per cent of the time, which is "show me open
 * urgent ones in my group". A generic row makes you name the field, choose
 * `is any of`, then find the value — three decisions to express one.
 *
 * So: sections for the common fields, with the FACET COUNTS beside each value
 * so you can see how much work is behind a choice before making it. The
 * expression builder stays, folded away under Advanced, because operators and
 * ALL/ANY nesting are real and sections cannot say them.
 *
 * Both write the SAME condition, so switching between them loses nothing.
 */
import { SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ConditionBuilder, toClauses, type ConditionField } from '../../components/ConditionBuilder';
import type { Condition } from '@itsm/shared';

export function QueueFilterPanel({
  value,
  onChange,
  onClose,
  fields,
  valueOptions,
}: {
  value: Condition | undefined;
  onChange: (c: Condition | undefined) => void;
  onClose: () => void;
  fields: ConditionField[];
  valueOptions: (key: string) => { value: string; label: string }[];
}) {
  const count = toClauses(value).clauses.length;

  return (
    <aside className="z-30 flex w-80 shrink-0 flex-col border-l bg-card">
      <div className="flex h-14 shrink-0 items-center gap-2 border-b px-3">
        <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">Filter</span>
        {count > 0 && (
          <span className="rounded bg-primary px-1.5 text-[11px] text-primary-foreground">{count}</span>
        )}
        <div className="flex-1" />
        {count > 0 && (
          <button
            onClick={() => onChange(undefined)}
            className="text-xs text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Clear
          </button>
        )}
        <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close filter">
          <X />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {/* ONE filter. Kai: *"I don't want a simple & advanced filter system.
            I want the advanced to look better, like the captures."*
            So the sections-plus-Advanced split is gone, and the builder itself
            is rendered the way the reference's panel reads: the field name as
            a heading with its control beneath, one block per condition. It is
            the same component the automation rule builder and the SLA editor
            use — a `layout` prop, not a second implementation. */}
        <ConditionBuilder
          value={value}
          onChange={onChange}
          fields={fields}
          valueOptions={valueOptions}
          defaultFieldKey="statusCategory"
          addLabel="Add a filter"
          layout="stacked"
        />
        <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
          Negations include tickets with no value at all — &ldquo;priority is not high&rdquo; returns
          unprioritised tickets, which plain SQL would silently drop.
        </p>
      </div>
    </aside>
  );
}
